import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useAuth0 } from "@auth0/auth0-react";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "@/lib/auth/auth-context";
import { fadeIn, slideUp, withReducedMotion } from "@/lib/animations";
import { handleAuth0Error } from "@/lib/auth/auth-errors";
import { AlertLiveRegion } from "@/components/a11y/LiveRegion";

export const Route = createFileRoute("/auth/login")({
  head: () => ({
    meta: [
      {
        name: "robots",
        content: "noindex, nofollow",
      },
    ],
  }),
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): { redirect: string; error?: string; error_description?: string } => {
    return {
      redirect: (search.redirect as string) || "/app/dashboard",
      error: search.error as string | undefined,
      error_description: search.error_description as string | undefined,
    };
  },
});

function LoginPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { loginWithRedirect, error: auth0Error } = useAuth0();
  const navigate = useNavigate();
  const search = useSearch({ from: "/auth/login" });
  const [error, setError] = useState("");
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate({ to: search.redirect });
    }
  }, [isAuthenticated, authLoading, navigate, search.redirect]);

  // Handle Auth0 errors from URL params (returned from callback)
  useEffect(() => {
    if (search.error) {
      const errorMessage = search.error_description || "Authentication failed. Please try again.";
      setError(errorMessage);
    }
  }, [search.error, search.error_description]);

  // Handle Auth0 SDK errors
  useEffect(() => {
    if (auth0Error) {
      const authError = handleAuth0Error(auth0Error);
      setError(authError.message);
    }
  }, [auth0Error]);

  const handleSignIn = async () => {
    setError("");
    setIsRedirecting(true);

    // Store redirect URL in sessionStorage for callback
    sessionStorage.setItem("auth_redirect", search.redirect);

    try {
      await loginWithRedirect({
        appState: { returnTo: search.redirect },
      });
    } catch (err) {
      const authError = handleAuth0Error(err);
      setError(authError.message);
      setIsRedirecting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setIsRedirecting(true);

    // Store redirect URL in sessionStorage for callback
    sessionStorage.setItem("auth_redirect", search.redirect);

    try {
      await loginWithRedirect({
        authorizationParams: {
          connection: "google-oauth2",
        },
        appState: { returnTo: search.redirect },
      });
    } catch (err) {
      const authError = handleAuth0Error(err);
      setError(authError.message);
      setIsRedirecting(false);
    }
  };

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-purple-50 to-white dark:from-slate-900 dark:to-slate-950">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-purple-50 to-white px-4 dark:from-slate-900 dark:to-slate-950">
      <motion.div
        className="w-full max-w-md"
        initial="hidden"
        animate="visible"
        variants={withReducedMotion(fadeIn)}
      >
        {/* Logo */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-purple-500">AerialFlow</h1>
        </div>

        {/* Card */}
        <motion.div
          className="rounded-2xl border border-slate-200 bg-white p-8 shadow-lg dark:border-slate-800 dark:bg-slate-900"
          variants={withReducedMotion(slideUp)}
        >
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              Welcome back
            </h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
              Sign in to your studio account
            </p>
          </div>

          {/* Error Alert */}
          <AlertLiveRegion>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 flex items-start gap-3 rounded-lg bg-red-50 p-4 text-sm text-red-800 dark:bg-red-950/30 dark:text-red-400"
                role="alert"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <span>{error}</span>
              </motion.div>
            )}
          </AlertLiveRegion>

          {/* Sign In Buttons */}
          <div className="space-y-4">
            {/* Primary Sign In Button */}
            <button
              type="button"
              onClick={handleSignIn}
              disabled={isRedirecting}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-purple-500 px-4 py-3 font-semibold text-white transition-colors hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-slate-900"
            >
              {isRedirecting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                  <span>Redirecting to secure login...</span>
                </>
              ) : (
                <span>Sign In</span>
              )}
            </button>

            {/* Divider */}
            <div className="flex items-center">
              <div className="flex-1 border-t border-slate-200 dark:border-slate-800" />
              <span className="px-4 text-sm text-slate-500 dark:text-slate-400">
                or
              </span>
              <div className="flex-1 border-t border-slate-200 dark:border-slate-800" />
            </div>

            {/* Google Sign In Button */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={isRedirecting}
              className="flex w-full items-center justify-center gap-3 rounded-lg border-2 border-slate-300 bg-white px-4 py-3 font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-750 dark:focus:ring-offset-slate-900"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span>Continue with Google</span>
            </button>
          </div>

          {/* Footer */}
          <p className="mt-6 text-center text-xs text-slate-500 dark:text-slate-400">
            Don't have an account?{" "}
            <a
              href="mailto:support@aerialflow.com"
              className="text-purple-500 hover:text-purple-600"
            >
              Contact your studio
            </a>
          </p>
        </motion.div>

        {/* Security Footer */}
        <p className="mt-4 text-center text-xs text-slate-500 dark:text-slate-400">
          Protected by industry-standard encryption
        </p>
      </motion.div>
    </div>
  );
}
